import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import GlobalSearchModal from './GlobalSearchModal';
import ShortcutsModal from './ShortcutsModal';
import Breadcrumb from './Breadcrumb';
import NotificationsPanel from './NotificationsPanel';
import { PageHistoryPanel } from './PageHistoryPanel';
import { useAuth } from '../../hooks/useAuth';
import { useRealtimeNotifications } from '../../hooks/useRealtimeNotifications';
import { supabase } from '../../lib/supabase';
import { useAccentColor } from '../../contexts/AccentColorContext';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { logger } from '../../lib/logger';

const CENTIF_ID = 'bdb7f28b-2034-4dd3-ab5b-aa59a1ed6f48';
const CENTIF_ROLES = ['admin', 'superviseur', 'analyste', 'enqueteur'];

const EXTERNAL_ROLES = ['agent_declarant', 'lecteur', 'collaborateur_national', 'collaborateur_international'];

const ROLE_BADGE: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  admin: { label: 'Administrateur', color: '#7c3aed', bg: '#ede9fe', icon: 'ri-shield-star-line' },
  superviseur: { label: 'Superviseur', color: '#0369a1', bg: '#e0f2fe', icon: 'ri-eye-line' },
  analyste: { label: 'Analyste', color: '#0f766e', bg: '#ccfbf1', icon: 'ri-search-eye-line' },
  enqueteur: { label: 'Enquêteur', color: '#b45309', bg: '#fef3c7', icon: 'ri-shield-user-line' },
  agent_declarant: { label: 'Agent Déclarant', color: '#be185d', bg: '#fce7f3', icon: 'ri-user-voice-line' },
  collaborateur_national: { label: 'Collaborateur National', color: '#1d4ed8', bg: '#dbeafe', icon: 'ri-global-line' },
  collaborateur_international: { label: 'Collaborateur Intl.', color: '#6d28d9', bg: '#ede9fe', icon: 'ri-earth-line' },
  lecteur: { label: 'Lecteur', color: '#374151', bg: '#f3f4f6', icon: 'ri-book-read-line' },
};

function getTheme(_role: string | undefined) {
  return {
    bg: '#ffffff',
    text: '#1f2937',
    subtext: '#6b7280',
    hover: 'rgba(0,0,0,0.05)',
    border: '#e5e7eb',
    searchBg: '#f3f4f6',
    searchBorder: '#e5e7eb',
    searchText: '#1f2937',
    searchPlaceholder: '#9ca3af',
    avatarBg: '#e5e7eb',
  };
}

export default function TopBar() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const theme = getTheme(user?.role);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [organismeNom, setOrganismeNom] = useState<string | null>(null);
  const { accentColor } = useAccentColor();

  const {
    notifications,
    unreadCount,
    loading: notifLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useRealtimeNotifications(user?.id);

  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [bellBounce, setBellBounce] = useState(false);
  const [shortcutToast, setShortcutToast] = useState<string | null>(null);
  const prevUnreadRef = useRef(unreadCount);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const historyRef = useRef<HTMLDivElement>(null);

  // Hook raccourcis clavier centralisé
  useKeyboardShortcuts({
    onOpenSearch: () => setShowSearch(true),
    onOpenHelp: () => setShowShortcuts(true),
  });

  // Charger l'avatar et le nom de l'organisme depuis Supabase
  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('users')
      .select('avatar_url')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.avatar_url) setAvatarUrl(data.avatar_url);
      });

    // Charger le nom de l'organisme
    const isCentifUser = user?.role ? CENTIF_ROLES.includes(user.role) : false;
    const orgId = isCentifUser ? CENTIF_ID : user?.organisation_id;
    if (orgId) {
      supabase
        .from('organisations')
        .select('nom')
        .eq('id', orgId)
        .maybeSingle()
        .then(({ data }) => {
          if (data?.nom) {
            // Raccourcir le nom CENTIF pour l'affichage
            if (isCentifUser) {
              setOrganismeNom('CENTIF');
            } else {
              setOrganismeNom(data.nom);
            }
          }
        });
    }
  }, [user?.id, user?.role, user?.organisation_id]);

  const handleSignOut = async () => {
    setShowProfile(false);
    try {
      await logout();
    } catch (error) {
      logger.error('Erreur lors de la déconnexion:', error);
    } finally {
      navigate('/login', { replace: true });
    }
  };

  useEffect(() => {
    if (unreadCount > prevUnreadRef.current) {
      setBellBounce(true);
      const timer = setTimeout(() => setBellBounce(false), 1000);
      return () => clearTimeout(timer);
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount]);

  // Toast raccourci
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.message) {
        setShortcutToast(detail.message);
        setTimeout(() => setShortcutToast(null), 2500);
      }
    };
    window.addEventListener('shortcut-toast', handler);
    return () => window.removeEventListener('shortcut-toast', handler);
  }, []);

  // Fermer le panneau historique au clic extérieur
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (historyRef.current && !historyRef.current.contains(event.target as Node)) {
        setShowHistory(false);
      }
    }
    if (showHistory) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showHistory]);

  // Ctrl+K pour la recherche
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      <style>{`
        @keyframes bellShake {
          0% { transform: rotate(0deg); }
          15% { transform: rotate(14deg); }
          30% { transform: rotate(-12deg); }
          45% { transform: rotate(10deg); }
          60% { transform: rotate(-8deg); }
          75% { transform: rotate(4deg); }
          90% { transform: rotate(-2deg); }
          100% { transform: rotate(0deg); }
        }
        .bell-shake { animation: bellShake 0.8s ease-in-out; }
        @keyframes badgePulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.2); }
        }
        .badge-pulse { animation: badgePulse 2s ease-in-out infinite; }
      `}</style>

      {/* Toast raccourci */}
      {shortcutToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] px-4 py-2.5 bg-gray-900 text-white text-sm rounded-xl shadow-xl flex items-center gap-2 pointer-events-none">
          <i className="ri-keyboard-line text-base"></i>
          {shortcutToast}
        </div>
      )}

      <header className="sticky top-0 z-50" style={{ backgroundColor: theme.bg, borderBottom: `1px solid ${theme.border}` }}>
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Breadcrumb */}
            <div style={{ color: theme.text }}>
              <Breadcrumb />
            </div>

            <div className="flex items-center space-x-2">
              {/* Recherche globale */}
              <button
                onClick={() => setShowSearch(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors cursor-pointer"
                style={{
                  backgroundColor: theme.searchBg,
                  borderColor: theme.searchBorder,
                  color: theme.searchText,
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.hover)}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = theme.searchBg)}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-line text-base"></i>
                </div>
                <span className="text-sm hidden lg:inline" style={{ color: theme.searchPlaceholder }}>Rechercher...</span>
                <kbd className="hidden lg:inline-flex items-center px-1.5 py-0.5 text-[10px] rounded border ml-2"
                  style={{ color: theme.searchPlaceholder, borderColor: theme.searchBorder, backgroundColor: 'rgba(255,255,255,0.08)' }}>
                  Ctrl+K
                </kbd>
              </button>

              {/* Bouton Raccourcis clavier */}
              <button
                onClick={() => setShowShortcuts(true)}
                className="relative p-2 rounded-lg transition-colors cursor-pointer group"
                style={{ color: theme.text }}
                title="Raccourcis clavier (Alt+?)"
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.hover)}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-keyboard-line text-xl"></i>
                </div>
                {/* Tooltip */}
                <span className="absolute -bottom-9 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-[11px] rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">
                  Raccourcis
                </span>
              </button>

              {/* Bouton Historique */}
              <div className="relative" ref={historyRef}>
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="relative p-2 rounded-lg transition-colors cursor-pointer group"
                  style={{ color: theme.text }}
                  title="Historique de navigation"
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.hover)}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-history-line text-xl"></i>
                  </div>
                  {/* Tooltip */}
                  <span className="absolute -bottom-9 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-[11px] rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">
                    Historique
                  </span>
                </button>

                {showHistory && <PageHistoryPanel onClose={() => setShowHistory(false)} />}
              </div>

              {/* Notifications */}
              <div className="relative" ref={notificationsRef}>
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 rounded-lg transition-colors cursor-pointer"
                  style={{ color: theme.text }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.hover)}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <div className={`w-5 h-5 flex items-center justify-center ${bellBounce ? 'bell-shake' : ''}`}>
                    <i className={`${unreadCount > 0 ? 'ri-notification-3-fill' : 'ri-notification-3-line'} text-xl`}></i>
                  </div>
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full badge-pulse shadow-sm">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                <NotificationsPanel
                  notifications={notifications}
                  unreadCount={unreadCount}
                  loading={notifLoading}
                  onMarkAsRead={markAsRead}
                  onMarkAllAsRead={markAllAsRead}
                  onDelete={deleteNotification}
                  isOpen={showNotifications}
                  onClose={() => setShowNotifications(false)}
                />
              </div>

              {/* Profil utilisateur */}
              <div className="relative">
                <button
                  onClick={() => setShowProfile(!showProfile)}
                  className="flex items-center gap-3 rounded-lg px-3 py-2 cursor-pointer transition-colors"
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.hover)}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
                    style={{ backgroundColor: theme.avatarBg }}
                  >
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt="Avatar"
                        className="w-full h-full object-cover"
                        onError={() => setAvatarUrl(null)}
                      />
                    ) : (
                      <span className="text-gray-700 text-sm font-medium">
                        {user?.prenom?.[0]}{user?.nom?.[0]}
                      </span>
                    )}
                  </div>
                  <div className="text-left hidden lg:block">
                    <p className="text-sm font-medium" style={{ color: theme.text }}>
                      {user?.prenom} {user?.nom}
                    </p>
                    <div className="flex items-center gap-1.5">
                      <p className="text-xs capitalize" style={{ color: theme.subtext }}>
                        {user?.role?.replace('...', ' ')}
                      </p>
                      
                    </div>
                  </div>
                  <i className="ri-arrow-down-s-line" style={{ color: theme.subtext }}></i>
                </button>

                {showProfile && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowProfile(false)}></div>
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center bg-gray-200">
                          {avatarUrl ? (
                            <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" onError={() => setAvatarUrl(null)} />
                          ) : (
                            <span className="text-gray-700 text-sm font-medium">{user?.prenom?.[0]}{user?.nom?.[0]}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{user?.prenom} {user?.nom}</p>
                          <p className="text-xs text-gray-500 mt-0.5 truncate">{user?.email}</p>
                          {user?.role && ROLE_BADGE[user.role] && (
                            <span
                              className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap"
                              style={{
                                color: ROLE_BADGE[user.role].color,
                                backgroundColor: ROLE_BADGE[user.role].bg,
                              }}
                            >
                              <i className={`${ROLE_BADGE[user.role].icon} text-xs`}></i>
                              {ROLE_BADGE[user.role].label}
                            </span>
                          )}
                          {/* Nom de l'organisme */}
                          {organismeNom && (
                            <div className="flex items-center gap-1 mt-1.5">
                              <i className="ri-building-2-line text-teal-500 text-xs"></i>
                              <span className="text-xs text-teal-700 font-medium truncate" title={organismeNom}>
                                {organismeNom}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="py-1">
                        <Link
                          to="/profil"
                          onClick={() => setShowProfile(false)}
                          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                        >
                          <i className="ri-user-line text-gray-400"></i>
                          Mon profil
                        </Link>
                        <Link
                          to="/mon-organisme"
                          onClick={() => setShowProfile(false)}
                          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                        >
                          <i className="ri-building-2-line text-gray-400"></i>
                          Mon organisme
                          {organismeNom && (
                            <span className="ml-auto text-xs text-teal-600 font-medium truncate max-w-[80px]">{organismeNom}</span>
                          )}
                        </Link>
                        <button
                          onClick={() => { setShowProfile(false); setShowShortcuts(true); }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-keyboard-line text-gray-400"></i>
                          Raccourcis clavier
                          <kbd className="ml-auto text-[10px] font-mono px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded text-gray-500">Alt+?</kbd>
                        </button>
                        {user?.role === 'admin' && (
                          <Link
                            to="/parametres"
                            onClick={() => setShowProfile(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                          >
                            <i className="ri-settings-3-line text-gray-400"></i>
                            Paramètres
                          </Link>
                        )}
                      </div>

                      <div className="border-t border-gray-100 py-1">
                        <button
                          onClick={handleSignOut}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-logout-box-line"></i>
                          Se déconnecter
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <GlobalSearchModal isOpen={showSearch} onClose={() => setShowSearch(false)} />
      <ShortcutsModal isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />
    </>
  );
}
